/*
 * Copyright (c) 2017 Datarista, Inc.
 */

import { Map, List, Record } from 'immutable';
import { Account as FullAccount } from './account';
import { actionTypes } from '../actions/superadmin';
import { Order } from './order';

const AccountEmail = new Record({
  id: 0,
  accountId: 0,
  email: '',
});

const SFTP = new Record({
  id: 0,
  accountId: 0,
  username: '',
  uid: 0,
  gid: 0,
  directory: '',
  shell: '',
});

const Platform = new Record({
  id: 0,
  description: '',
  code: '',
  platformType: '',
});

export const Account = new Record({
  id: 0,
  companyName: '',
  status: 0,
  accountTypeId: 0,
  address1: '',
  address2: '',
  city: '',
  stateProvince: '',
  postcode: '',
  country: '',
  url: '',
  portalHost: '',
  phone: '',
});

const Job = new Record({
  id: '',
  installationId: '',
  parentId: '',
  source: '',
  status: '',
  statusDetail: '',
  createdOn: '',
  updatedOn: '',
});

const Batch = new Record({
  jobId: '',
  batchNumber: '',
  status: '',
  statusDetail: '',
  createdOn: '',
  updatedOn: '',
});

const Fileset = new Record({
  id: 0,
  datasetId: 0,
  dataFilenamePattern: '',
  layoutFilename: '',
  recordLevelTypeId: 0,
});

const Layout = new Record({
  id: 0,
  datasetId: 0,
  filesetId: 0,
  name: '',
  position: 0,
  dataType: '',
  length: 0,
  format: '',
  description: '',
  standardFieldId: 0,
  uiSelectionType: '',
  uiDisplay: 0,
  isPrimaryKey: false,
  parentFilesetField: 0,
});

export const Dataset = new Record(
  {
    id: 0,
    accountId: 0,
    providerDataSetNum: '',
    name: '',
    listType: '',
    listGeo: '',
    status: 0,
    code: '',
    s3Path: '',
    searchHost: '',
    customType: '',
    incrementalUpdates: false,
    autoVersionUpdates: false,
    createdBy: '',
    createdOn: '',
    updatedBy: '',
    updatedOn: '',
    state: '',
  },
  'Dataset'
);

export const RecordLevelType = new Record(
  {
    id: 0,
    code: '',
    listType: '',
    description: '',
  },
  'RecordLevelType'
);

export const Field = new Record({
  id: 0,
  datasetId: 0,
  filesetId: 0,
  name: '',
  position: 0,
  dataType: '',
  length: 0,
  format: '',
  description: '',
  standardFieldId: 0,
  uiSelectionType: '',
  uiDisplay: 0,
  isPrimaryKey: false,
  parentFilesetField: 0,
});

const Config = new Record({
  isEdit: false,
  data: {
    installUrl: '',
    fieldNamespace: '',
    consumerKey: '',
    consumerSecret: '',
    partnerKey: '',
    code: '',
    objectName: {
      accountObject: '',
      contactObject: '',
      leadAccountObject: '',
      leadContactObject: '',
      itemObject: '',
    },
    prospecting: {
      tabs: [
        {
          type: 'B2B_COMPANY',
          objectName: null,
          tabName: 'Company',
          enabled: true,
          displayFields: [],
        },
        {
          type: 'B2B_CONTACT',
          objectName: null,
          tabName: 'Contact',
          enabled: true,
          displayFields: [],
        },
      ],
    },
  },
});

const superadmin = new Map({
  accounts: new List(),
  currAccount: new FullAccount(),
  jobs: new List(),
  batches: new List(),
  sftpInfo: new SFTP(),
  dataset: new Dataset(),
  platforms: new List(),
  configInfo: new Config(),
  filesets: new Map(),
  currPage: 0,
  filesetValues: new Fileset(),
  newLayout: new Layout(),
  fieldSummary: new Map(),
  datasetIdForFileset: 0,
  datasetNameForFileset: '',
  selectedAccount: new Account(),
  viewOnlyAccount: new Account(),
  recordLevelTypes: new List(),
  orders: new List(),
  accountCCEmails: new List(),
});

const initialState = superadmin;

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.ORDER_FETCH_ALL_BY_ACCOUNT_SUCCESS: {
      const newOrdersArray = action.orders.map(order => new Order(order));
      const newOrdersList = new List(newOrdersArray);
      return state.merge({ orders: newOrdersList });
    }
    case actionTypes.GET_ACCOUNT_BY_ID_SUCCESS: {
      const currAccount = new FullAccount(action.currAccount);
      return state.merge({ currAccount });
    }
    case actionTypes.VIEW_USER_TYPES: {
      const isA = action.isAdmin;
      const vOA = new Account(action.viewOnlyAccount);
      return state.merge({
        isAdmin: isA,
        viewOnlyAccount: vOA,
      });
    }
    case actionTypes.COMPANY_ACCOUNTS_FETCH_SUCCESS: {
      const newAccountsArray = action.accounts.map(account => new Account(account));
      const newAccountsList = new List(newAccountsArray);
      return state.merge({ accounts: newAccountsList });
    }
    case actionTypes.JOBS_FETCH_SUCCESS: {
      const newJobsArray = action.jobs.map(job => new Job(job));
      const newJobsList = new List(newJobsArray);
      return state.merge({ jobs: newJobsList });
    }
    case actionTypes.BATCH_FETCH_SUCCESS: {
      const newBatchesArray = action.batches.map(batch => new Batch(batch));
      const newBatchesList = new List(newBatchesArray);
      return state.merge({ batches: newBatchesList });
    }
    case actionTypes.INVITE_NEW_USER: {
      return state;
    }
    case actionTypes.RECEIVE_FILESETS: {
      let newFilesetMap = new Map();
      action.filesets.map(fileset => {
        newFilesetMap = newFilesetMap.set(fileset.id, fileset);
        return newFilesetMap;
      });
      return state.merge({ filesets: newFilesetMap });
    }
    case actionTypes.CHANGE_PAGE: {
      const fileset = new Fileset({
        id: action.values.id,
        datasetId: 0,
        dataFilenamePattern: action.values.dataFilenamePattern,
        layoutFilename: action.values.layoutFilename,
        recordLevelTypeId: action.values.recordLevelTypeId,
      });
      return state.merge({ currPage: action.nextPage, filesetValues: fileset });
    }
    case actionTypes.FILESET_ACTION_SUCCESS: {
      const prevList = state.get('filesets');
      const newList = prevList.set(action.fileset.id, action.fileset);
      return state.merge({ currPage: 0, filesets: newList });
    }
    case actionTypes.DELETE_FILESET_SUCCESS: {
      const prevList = state.get('filesets');
      const newList = prevList.delete(action.fileset.id);
      return state.merge({ filesets: newList });
    }
    case actionTypes.LAYOUT_UPLOAD: {
      const layout = new Layout(action.layoutInfo);
      return state.merge({ newLayout: layout, currPage: 0 });
    }
    case actionTypes.RETRIEVE_CREDENTIALS_SUCCESS: {
      const newSftpInfo = new SFTP(action.sftpInfo);
      return state.merge({ sftpInfo: newSftpInfo });
    }
    case actionTypes.DATASET_FETCH_SUCCESS: {
      const newDataset = new Dataset(action.dataset);
      return state.merge({ dataset: newDataset });
    }
    case actionTypes.RECEIVE_FIELDS: {
      const fieldRecords = action.fields.map(field => new Field(field));
      const currFieldSummary = state.get('fieldSummary');
      const fieldMap = currFieldSummary.set(action.filesetId, fieldRecords);
      return state.merge({ fieldSummary: fieldMap });
    }
    case actionTypes.OPEN_FILESET_PAGE: {
      return state.merge({
        datasetIdForFileset: action.datasetId,
        datasetNameForFileset: action.datasetName,
      });
    }
    case actionTypes.RECEIVE_PLATFORMS: {
      const newPlatformsArray = action.platforms.map(platform => new Platform(platform));
      const newPlatformList = new List(newPlatformsArray);
      return state.merge({ platforms: newPlatformList });
    }
    case actionTypes.CREATE_CONFIG_SUCCESS:
    case actionTypes.RECEIVE_CONFIG_SETTINGS:
    case actionTypes.UPDATE_CONFIG_SUCCESS: {
      const { configSettings } = action;
      if (configSettings && configSettings.configuration) {
        const configInfo = new Config({ isEdit: true, data: configSettings.configuration });
        return state.merge({ configInfo });
      }
      const configInfo = new Config();
      return state.merge({ configInfo });
    }
    case actionTypes.RETRIVE_RECORD_LEVELS_SUCCESS: {
      const recordLevelTypes = action.recordLevels.map(type => new RecordLevelType(type));
      return state.merge({ recordLevelTypes });
    }
    case actionTypes.SET_SELECTED_ACCOUNT_SUCCESS: {
      const selectedAccount = new Account(action.account);
      return state.merge({ selectedAccount });
    }
    case actionTypes.RECEIVE_ACCOUNT_CC_EMAILS_SUCCESS: {
      const newEmailsArray = action.accountEmails.map(email => new AccountEmail(email));
      const accountCCEmails = new List(newEmailsArray);
      return state.merge({ accountCCEmails });
    }
    default: {
      return state;
    }
  }
}
